import {
  useIsMobile,
  WSButton,
  WSContainer,
  WSDivider,
  WSElement,
  WSFlexBox,
  WSIcon,
  WSPanel,
  WSStatus,
  WSStatusType,
  WSTabs,
  WSText,
  WSTodoList
} from "@wingspanhq/fe-component-library";
import moment from "moment";
import React from "react";
import { useHistory } from "react-router-dom";
import { useBrowserPageTitle } from "../../components/BrowserPageTitle/BrowserPageTitle";
import { WSPersistentUpgradeButton } from "../../components/Membership/WSPersistentUpgradeButton";
import { OverlaySpinner } from "../../components/OverlaySpinner";
import { useVerificationUpgrade } from "../../Invoices/components/VerificationUpgrade/VerificationUpgrade";
import { useFeatureFlags } from "../../query/hooks/useFeatureFlags";
import { WSFrontendFeature } from "../../Settings/utils/subscriptionUtils";
import { openInNewTab } from "../../utils/openInNewTab";
import {
  ADD_TO_CALENDAR_LINK,
  getCurrentIRSQuarter
} from "../utils/getIRSQuarter";
import styles from "./TaxesMainMobile.module.scss";
import { TaxesModalTurnOnPayment } from "./TaxesModalTurnOnPayment";
import { TaxesModalTurnOnWithholdings } from "./TaxesModalTurnOnWithholdings";

const year = moment().year();

const quarters = [
  {
    title: `Q1: Jan 1 - Mar 31, ${year}`,
    date: `Due Apr 15, ${year}`,
    description: `Recommend sending payment Apr 1, ${year}`
  },
  {
    title: `Q2: Apr 1 - May 31, ${year}`,
    date: `Due Jun 15, ${year}`,
    description: `Recommend sending payment Jun 1, ${year}`
  },
  {
    title: `Q3: Jun 1 - Aug 31, ${year}`,
    date: `Due Sept 15, ${year}`,
    description: `Recommend sending payment Sept 1, ${year}`
  },
  {
    title: `Q4: Sept 1 - Dec 31, ${year}`,
    date: `Due Jan 15, ${year + 1}`,
    description: `Recommend sending payment Jan 1, ${year + 1}`
  }
];

const quarter = getCurrentIRSQuarter();

export const TaxesMainMobilePreview: React.FC = () => {
  const history = useHistory();
  const isMobile = useIsMobile();

  useBrowserPageTitle("Taxes Overview");

  const currentDueDate = moment(quarter.dueDate, "MMM DD, YYYY");
  const deadlineFromNow = currentDueDate.fromNow(true);

  const {
    requestVerification,
    requestVerificationMeta,
    isBankingRequested
  } = useVerificationUpgrade();
  const featureFlags = useFeatureFlags();

  const startTaxesOnboarding = async () => {
    try {
      if (featureFlags.data?.verificationsByFeature && !isBankingRequested) {
        await requestVerification({
          banking: true
        });
      }

      history.push("/member/taxes/onboarding/filing-status");
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <WSContainer verticalPadding>
      {requestVerificationMeta.isLoading && <OverlaySpinner />}
      <TaxesModalTurnOnPayment />
      <TaxesModalTurnOnWithholdings />
      <WSFlexBox.CenterY justify="space-between">
        <WSText.Heading1>Taxes</WSText.Heading1>
        <WSStatus status={WSStatusType.Warning}>
          <WSText.Label>
            Withholdings off
            {". "}
            <WSButton.Link inline onClick={startTaxesOnboarding}>
              Turn on.
            </WSButton.Link>
          </WSText.Label>
        </WSStatus>
      </WSFlexBox.CenterY>

      <WSTabs
        mt="M"
        tabs={[
          {
            label: "Withholdings",
            render: () => (
              <>
                <WSText.Label color="grey600" mb="XS" align="center">
                  Current balance set aside for taxes
                </WSText.Label>
                <WSText.Display mb="XS" align="center">
                  $—
                </WSText.Display>
                <WSFlexBox.CenterX my="S">
                  <WSText.Heading5 inline align="center">
                    Next quarterly payment due {isMobile ? <br /> : null} in{" "}
                    {deadlineFromNow} (<b>{currentDueDate.format("MMM DD")}</b>
                    ).
                    <WSIcon
                      name="calendar"
                      size="S"
                      ml="XS"
                      mb="-XS"
                      color="blue500"
                      onClick={() => openInNewTab(ADD_TO_CALENDAR_LINK)}
                    />
                  </WSText.Heading5>
                </WSFlexBox.CenterX>
                <WSPersistentUpgradeButton
                  feature={WSFrontendFeature.TaxWitholdings}
                  fullWidth
                  onClick={startTaxesOnboarding}
                  data-testid="startOnboarding"
                >
                  Set up tax withholdings
                </WSPersistentUpgradeButton>
                <WSPanel mt="M">
                  <WSElement className={styles.logo} mb="M">
                    <WSIcon name="taxProfile" size="M" color="grey700" />
                  </WSElement>
                  <WSText.Heading3>Set aside money for taxes</WSText.Heading3>
                  <WSText.Paragraph my="S" color="grey600">
                    Automate tax withholdings with Wingspan
                  </WSText.Paragraph>

                  <WSTodoList
                    items={[
                      {
                        mark: "1.",
                        content: (
                          <>
                            <WSText.LabelBold>
                              Estimate your tax rate
                            </WSText.LabelBold>
                            <WSText.Label mt="XS">
                              Find our recommendation specific to you
                            </WSText.Label>
                          </>
                        )
                      },
                      {
                        mark: "2.",
                        content: (
                          <>
                            <WSText.LabelBold>
                              Start setting aside
                            </WSText.LabelBold>
                            <WSText.Label mt="XS">
                              Automatically set aside your tax rate from all
                              Wingspan payments
                            </WSText.Label>
                          </>
                        )
                      },
                      {
                        mark: "3.",
                        content: (
                          <>
                            <WSText.LabelBold>
                              Watch your withholdings grow
                            </WSText.LabelBold>
                            <WSText.Label mt="XS">
                              Wingspan holds your tax withholdings, withdraw
                              when you need!
                            </WSText.Label>
                          </>
                        )
                      },
                      {
                        mark: "4.",
                        content: (
                          <>
                            <WSText.LabelBold>Pay the IRS</WSText.LabelBold>
                            <WSText.Label mt="XS">
                              Send 1-click payments on time each quarter
                            </WSText.Label>
                          </>
                        )
                      }
                    ]}
                  />

                  <WSDivider my="M" />
                  <WSButton.Link
                    rightIcon="chevronRight"
                    onClick={startTaxesOnboarding}
                  >
                    Continue to Step 1: Find your tax rate
                  </WSButton.Link>
                </WSPanel>
              </>
            )
          },
          {
            label: "Tax rate",
            render: () => (
              <>
                <WSText.Label color="grey600" mb="XS" align="center">
                  My withholding rate is
                </WSText.Label>
                <WSText.Display mb="XS" align="center">
                  —%
                </WSText.Display>
                <WSText.Heading5 color="grey600" mb="M" align="center">
                  Most accurate estimate based on tax profile
                </WSText.Heading5>
                <WSButton.Secondary fullWidth onClick={startTaxesOnboarding}>
                  Find my tax rate
                </WSButton.Secondary>
                <WSText.Label my="M">
                  This is the proportion of 1099 income you should set aside to
                  be financially prepared for taxes.
                </WSText.Label>
                <WSText.Heading4 my="M">
                  2020 quarterly estimated payment schedule
                </WSText.Heading4>
                {quarters.map(({ title, description, date }) => (
                  <WSElement pb="S" key={title}>
                    <WSDivider mb="S" />
                    <WSFlexBox.CenterY justify="space-between">
                      <WSText.Paragraph color="grey600">
                        {title}
                      </WSText.Paragraph>
                      <WSText.Paragraph color="grey700">
                        {date}
                      </WSText.Paragraph>
                    </WSFlexBox.CenterY>
                    <WSText.Label mt="XS" color="grey600">
                      {description}
                    </WSText.Label>
                  </WSElement>
                ))}
              </>
            )
          }
        ]}
      />
    </WSContainer>
  );
};
